import React, { Component } from 'react';
import { View, BackHandler, Keyboard, Platform } from 'react-native';

import { PasswordInput, VFButton, VFInput } from '@vfgroup-oneplatform/foundation/Components';
import { withTheme, LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { ToBiEmojis } from '@vfgroup-oneplatform/foundation/ToBi';
import Navigation from '@vfgroup-oneplatform/foundation/Utils/Navigation';
import { find as _find, isFunction as _isFunction } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import BlockBox from '../../Components/BlockBox';
import { getThemeImages } from '../../Themes';
import Status from '../../Utils/AuthStatusEnum';
import LoginTestKeys from '../../Utils/LoginTestKeys';
import AuthContainer from '../AuthContainer/AuthContainer';

import styles from './Register.Styles';

class Register extends Component {
  constructor(props) {
    super(props);
    this.state = {
      startAnimation: false,
      values: {
        phoneNumber: '',
        password: '',
        confirmationPassword: '',
      },
      errors: {},
      showErrorBox: false,
      errorMessage: '',
      subErrorMessage: '',
      showPasswordIcon: false,
      tobiEmoji: undefined,
      isLoading: false,
      showBlockDialog: false,
    };
    this.timeOut = {};
    this.validations = {
      phoneNumber: this.handlePhoneValidation,
      confirmationPassword: this.handleConfirmationPasswordValidation,
      password: this.handlePasswordValidation,
    };
    this.phoneNumber = '';
    this.phoneRef = React.createRef();
    this.passwordRef = React.createRef();
    this.confirmPasswordRef = React.createRef();
  }
  componentWillUnmount() {
    for (const fieldKey in this.timeOut) {
      clearTimeout(this.timeOut[fieldKey]);
    }
  }

  onPressUseCredentials = () => {
    const onUpfrontLogin = Navigation.instance.getParam(this, 'onUpfrontLogin', () => null);
    onUpfrontLogin();
  };

  onRightIconPress = () => {
    this.setState({ startAnimation: true });
  };

  onEndAnimation = () => {
    this.setState({ startAnimation: false });
  };
  onRequestFailed = error => {
    const { errorMessage, subErrorMessage } = error;
    this.setState({
      showErrorBox: true,
      errorMessage,
      subErrorMessage,
      tobiEmoji: ToBiEmojis.SadWithoutTransition,
      isLoading: false,
    });
  };
  onGetSMSCodeSuccess = () => {
    const { values } = this.state;
    const { phoneNumber } = values;
    const onNext = Navigation.instance.getParam(this, 'onNext', () => null);
    onNext(phoneNumber);
    this.setState({ isLoading: false });
  };
  onRegisterSuccess = () => {
    const { values } = this.state;
    const { phoneNumber } = values;
    const getSMSCode = Navigation.instance.getParam(this, 'getSMSCode', () => null);
    getSMSCode(phoneNumber).then(this.onGetSMSCodeSuccess).catch(this.onRequestFailed);
  };
  onRegisterPress = async () => {
    Keyboard.dismiss();
    const { values } = this.state;
    const { phoneNumber, password } = values;
    const onRegister = Navigation.instance.getParam(this, 'onRegister', () => null);
    this.setState({
      showErrorBox: false,
      tobiEmoji: ToBiEmojis.Whistle,
      isLoading: true,
    });
    try {
      const result = await onRegister(phoneNumber, password);
      this.setState({
        tobiEmoji: undefined,
      });
      switch (result.status) {
        case Status.Success:
          this.onRegisterSuccess();
          break;
        case Status.Failed:
          this.onRequestFailed(result);
          break;
      }
    } catch (error) {
      this.setState({
        tobiEmoji: undefined,
        isLoading: false,
      });
      switch (error.status) {
        case Status.Failed:
          this.setState({
            showErrorBox: true,
            errorMessage: error.errorMessage,
            subErrorMessage: error.subErrorMessage,
            tobiEmoji: ToBiEmojis.SadWithoutTransition,
            isLoading: false,
          });
          break;
        case Status.Blocked:
          BackHandler.addEventListener('hardwareBackPress', this.handleBackButton);
          this.setState({
            showBlockDialog: true,
            tobiEmoji: ToBiEmojis.Surprise,
          });
          break;
      }
    }
  };
  getIsFormErrorState = () => {
    const { errors, values } = this.state;
    const errExist = _find(errors, d => (d.error && d.validated) || !d.validated);
    const AllInputsValidated = Object.keys(errors).length == Object.keys(values).length;
    return errExist || !AllInputsValidated;
  };
  getErrorFieldProps = isError => {
    return {
      ...this.props.theme.colors.textInput[isError ? 'error' : 'default'],
      showHelperText: isError,
    };
  };
  getErrorProps = (showErrorBox, errorMessage, subErrorMessage) => {
    return {
      showErrorBox,
      errorMessage,
      subErrorMessage,
    };
  };
  getErrorPropsMemoized = memoize(this.getErrorProps);
  getErrorFieldPropsMemoized = memoize(this.getErrorFieldProps);
  handleResetFieldValidationState = key => {
    this.setState(state => ({
      errors: {
        ...state.errors,
        [key]: { ...state.errors[key], validated: false },
      },
    }));
  };
  handleValidation = (key, value, validate) => {
    this.handleResetFieldValidationState(key);
    if (!value) {
      return;
    }
    if (this.timeOut[key]) {
      clearTimeout(this.timeOut[key]);
    }
    this.timeOut[key] = setTimeout(() => validate(value), 800);
  };
  handleUpdateErrState = (key, isFieldValid) => {
    this.setState(state => {
      return {
        errors: {
          ...state.errors,
          [key]: { error: !isFieldValid, validated: true },
        },
      };
    });
  };
  handlePhoneValidation = value => {
    const validatePhone = Navigation.instance.getParam(this, 'validatePhone', () => true);
    const isPhoneValid = validatePhone(value);
    this.handleUpdateErrState('phoneNumber', isPhoneValid);
  };
  handlePasswordValidation = value => {
    const { values } = this.state;
    const { confirmationPassword } = values;
    const validatePassword = Navigation.instance.getParam(this, 'validatePassword', () => true);
    const isPasswordValid = validatePassword(value);
    this.handleUpdateErrState('password', isPasswordValid);
    if (confirmationPassword) {
      this.handleConfirmationPasswordValidation();
    }
  };

  handleConfirmationPasswordValidation = () => {
    const { values } = this.state;
    const { password, confirmationPassword } = values;
    const isValid = confirmationPassword == password;
    this.handleUpdateErrState('confirmationPassword', isValid);
  };
  handleInputChange = (fieldName, value) => {
    const { values } = this.state;
    if (fieldName === 'phoneNumber') {
      this.phoneNumber = value;
    }
    this.setState(
      {
        values: { ...values, [fieldName]: value },
        showErrorBox: false,
        tobiEmoji: undefined,
      },
      this.handleValidation(fieldName, value, this.validations[fieldName]),
    );
  };

  shouldShowErrorText = key => {
    const { errors, values } = this.state;
    return errors[key]?.error && errors[key]?.validated && values[key];
  };

  handleBackButton = () => {
    this.closeBlockDialog();
    return true;
  };

  changeAccount = () => {
    const isePhoneField =
      this.phoneRef && this.phoneRef.current && _isFunction(this.phoneRef.current.setValue);

    const isePasswordField =
      this.passwordRef &&
      this.passwordRef.current &&
      _isFunction(this.passwordRef.current.setValue);

    const iseConfirmPasswordField =
      this.confirmPasswordRef &&
      this.confirmPasswordRef.current &&
      _isFunction(this.confirmPasswordRef.current.setValue);

    isePhoneField && this.phoneRef.current.setValue('');
    isePasswordField && this.passwordRef.current.setValue('');
    iseConfirmPasswordField && this.confirmPasswordRef.current.setValue('');

    this.phoneNumber = '';

    this.setState(
      {
        values: {
          phoneNumber: '',
          password: '',
          confirmationPassword: '',
        },
        showBlockDialog: false,
        tobiEmoji: undefined,
      },
      () => {
        this.handleValidation(
          'phoneNumber',
          this.state.values.phoneNumber,
          this.validations.phoneNumber,
        );
        this.handleValidation('password', this.state.values.phoneNumber, this.validations.password);
        this.handleValidation(
          'confirmationPassword',
          this.state.values.phoneNumber,
          this.validations.confirmationPassword,
        );
      },
    );
    Keyboard.dismiss();
    BackHandler.removeEventListener('hardwareBackPress', this.handleBackButton);
  };

  resetAccount = () => {
    BackHandler.removeEventListener('hardwareBackPress', this.handleBackButton);
    const onResetAccount = Navigation.instance.getParam(this, 'onResetAccount', () => null);
    onResetAccount(this.phoneNumber);
    this.changeAccount();
  };

  closeBlockDialog = () => {
    this.setState({
      showBlockDialog: false,
    });
  };

  render() {
    const {
      values,
      startAnimation,
      showErrorBox,
      errorMessage,
      subErrorMessage,
      tobiEmoji,
      isLoading,
      showBlockDialog,
    } = this.state;
    const { password, confirmationPassword, phoneNumber } = values;
    const { theme, navigation } = this.props;
    const images = getThemeImages(theme.name);
    const renderTitle = !isLoading
      ? Navigation.instance.getParam(this, 'renderTitle', () => { })
      : () => { };
    return (
      <React.Fragment>
        <AuthContainer
          bubbleText="register_helper_information"
          tobiSize={114}
          renderTobiLessIconView={Navigation.instance.getParam(this, 'renderLoginIconView', null)}
          errorProps={this.getErrorPropsMemoized(showErrorBox, errorMessage, subErrorMessage)}
          onEndAnimation={this.onEndAnimation}
          childrenContainerAccessibilityLabel={LoginTestKeys.childrenContainer}
          firstLabelTitle="register_registered_with_my_vodafone_label"
          firstLabelDisabled
          secondLabelTitle="register_use_your_credentials"
          secondLabelOnPress={this.onPressUseCredentials}
          startAnimation={startAnimation}
          tobiIconAccessibilityLabel={LoginTestKeys.authContainerTobiIcon}
          firstLabelAccessibilityLabel={LoginTestKeys.loginNotRegisteredTxt}
          secondLabelAccessibilityLabel={LoginTestKeys.loginRegisterNowLink}
          tobiEmoji={tobiEmoji}
          isLoading={isLoading}
          loadingText="register_verifying_loading_text"
          renderTitle={renderTitle}
          {...Platform.select({
            web: {
              backBtn: true,
              backBtnPress: () => navigation?.pop(1)
            },
            default: {

            }
          })
          }
        >
          <View style={styles.containerStyle}>
            <VFInput
              label="register_phone_number_hint"
              rightIconName={images.ic_info_circle}
              showRightIcon
              {...this.getErrorFieldPropsMemoized(this.shouldShowErrorText('phoneNumber'))}
              helperText={'register_error_enter_valid_phone'}
              labelTextStyle={styles.labelTextStyle}
              value={phoneNumber}
              onChangeText={value => this.handleInputChange('phoneNumber', value)}
              rightIconPress={this.onRightIconPress}
              rightIconAccessibilityLabel={LoginTestKeys.VFInputRightIcon}
              inputAccessibilityLabel={LoginTestKeys.loginEmailTxtInput}
              helperAccessibilityLabel={LoginTestKeys.helperText}
              selectionColor={theme.colors.textInput.default.selectionColor}
              textColor={theme.colors.textColor}
              helperTextColor={theme.colors.textInput.error.tintColor}
              fieldRef={this.phoneRef}
            />

            <PasswordInput
              label="register_password_hint"
              value={password}
              {...this.getErrorFieldPropsMemoized(this.shouldShowErrorText('password'))}
              helperText={'register_error_enter_valid_password'}
              onChangeText={value => {
                this.handleInputChange('password', value);
              }}
              labelTextStyle={styles.labelTextStyle}
              containerStyle={styles.passwordInputStyle}
              inputAccessibilityLabel={LoginTestKeys.loginPassTxtInput}
              rightIconAccessibilityLabel={LoginTestKeys.passInputRightIcon}
              selectionColor={theme.colors.textInput.default.selectionColor}
              textColor={theme.colors.textColor}
              helperTextColor={theme.colors.textInput.error.tintColor}
              fieldRef={this.passwordRef}
            />
            <PasswordInput
              label="register_confirm_password_hint"
              value={confirmationPassword}
              onChangeText={value => this.handleInputChange('confirmationPassword', value)}
              {...this.getErrorFieldPropsMemoized(this.shouldShowErrorText('confirmationPassword'))}
              helperText={'register_error_enter_valid_confirmation_password'}
              labelTextStyle={styles.labelTextStyle}
              containerStyle={styles.passwordInputStyle}
              inputAccessibilityLabel={LoginTestKeys.loginPassTxtInput}
              rightIconAccessibilityLabel={LoginTestKeys.rightIconAccessibilityLabel}
              selectionColor={theme.colors.textInput.default.selectionColor}
              textColor={theme.colors.textColor}
              helperTextColor={theme.colors.textInput.error.tintColor}
              fieldRef={this.confirmPasswordRef}
            />

            <VFButton
              title="register_login_text"
              style={styles.buttonStyle}
              textStyle={styles.buttonTextStyle}
              disabled={this.getIsFormErrorState()}
              onPress={this.onRegisterPress}
              testID={LoginTestKeys.loginButton}
              textAccessibilityLabel={LoginTestKeys.loginButton}
              disabledStyle={styles.disabledBUttonStyle(theme)}
              disabledTextStyle={styles.disabledTextStyle(theme)}
              type="primary"
            />
          </View>
        </AuthContainer>
        {showBlockDialog ? (
          <BlockBox
            onPrimaryBtnPress={this.resetAccount}
            primaryBtnTitle="register_reset_password_button"
            onSecondaryBtnPress={this.changeAccount}
            secondaryBtnTitle="register_different_account_button"
            text="register_already_exists_warning_text"
            textPlaceholder={phoneNumber}
            onClose={this.closeBlockDialog}
            theme={theme}
            title="register_already_exists_warning_title"
            subtitle="register_already_exists_warning_subtitle"
          />
        ) : null}
      </React.Fragment>
    );
  }
}

Register.defaultProps = {
  theme: LightThemeColors,
};

Register.propTypes = {
  theme: PropTypes.object,
  navigation: PropTypes.object.isRequired,
};

export default withTheme(Register);
